
import React, { useState, useRef } from 'react';
import { ArrowLeft, Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StandardizedConnection } from '@/types/connections';
import { processFileUpload } from '@/utils/fileApi';
import UnifiedCsvImport from './UnifiedCsvImport';

interface FileImportViewProps {
  onBack: () => void;
  onSuccess: (connection: StandardizedConnection) => Promise<void>;
  isConnecting: boolean;
  onImportStart: () => void;
}

const FileImportView: React.FC<FileImportViewProps> = ({
  onBack,
  onSuccess,
  isConnecting,
  onImportStart
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const isCSVFile = (file: File): boolean => {
    return file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadError(null);
      onImportStart();

      const connectionId = `file-import-${Date.now()}`;
      
      if (!isCSVFile(selectedFile)) {
        // Handle non-CSV files - regular file upload to file_imports
        const result = await processFileUpload(selectedFile, connectionId);

        if (result.error) {
          throw new Error(result.error);
        }

        if (result.data) {
          const connection: StandardizedConnection = {
            id: result.data.connection_id,
            name: `File Import: ${selectedFile.name}`,
            status: 'connected',
            logoUrl: "https://via.placeholder.com/40?text=FILE",
            service: 'file',
            connectorId: result.data.connection_id,
            description: `${result.data.file_type} file: ${selectedFile.name}`,
            connectionId: result.data.connection_id,
            type: result.data.file_type,
            created_at: new Date().toISOString(),
            metadata: {
              file_name: selectedFile.name,
              file_size: selectedFile.size,
              file_type: result.data.file_type,
              storage_path: result.data.storage_path
            },
            file_name: selectedFile.name
          };

          await onSuccess(connection);
        }
      }
    } catch (error) {
      console.error('File upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleCsvSuccess = async (connection: StandardizedConnection) => {
    await onSuccess(connection);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeMessage = () => {
    if (!selectedFile) return 'Supports all file types including CSV, Excel, PDF, images, and more';
    
    if (isCSVFile(selectedFile)) {
      return 'CSV file detected - will be processed as a database import for analysis';
    }
    
    return 'File will be stored for future reference and access';
  };

  // Show CSV import interface for CSV files
  if (selectedFile && isCSVFile(selectedFile)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <h2 className="text-xl font-semibold">Import CSV File</h2>
        </div>

        <UnifiedCsvImport 
          onSuccess={handleCsvSuccess}
          onImportStart={onImportStart}
          isConnecting={isConnecting}
          shouldSaveDraft={false}
          mode="standard"
          preSelectedFile={selectedFile}
        />

        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onBack} disabled={uploading}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        <h2 className="text-xl font-semibold">Import File</h2>
      </div>

      <div className="space-y-4">
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="file-drop-zone"
        >
          <label htmlFor="file-upload" className="sr-only">
            Upload file
          </label>
          <input
            id="file-upload"
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept="*/*"
            data-testid="general-file-input"
          />

          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {selectedFile ? 'File Selected' : 'Choose a file or drag it here'}
          </p>
          <p className="text-sm text-gray-500">
            {selectedFile ? selectedFile.name : getFileTypeMessage()}
          </p>
        </div>

        {selectedFile && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
          </div>
        )}

        {uploadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-red-800">
              <AlertCircle size={16} />
              <span className="text-sm font-medium">Upload Error</span>
            </div>
            <p className="text-sm text-red-600 mt-1">{uploadError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onBack} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            disabled={!selectedFile || uploading || isConnecting}
          >
            {uploading ? 'Processing...' : 'Upload File'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FileImportView;
